import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FC } from "react";
import { ArrowForward, InfoOutlined } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { translate } from "@shape/reef-shared";
import { useFormState, type UseFormReturn } from "react-hook-form";
import type {
  AffectedAreaAccordionResponse,
  AffectedAreaMahResponse,
  AffectedAreaRowResponse,
  AffectedAreaSelectionFormValue,
  AffectedAreaTagSummaryResponse,
  CreateStep2MdbFormValues,
} from "types";
import { SectionTitle } from "../../../SectionTitle";
import styles from "./AffectedAreas.module.scss";

type AccordionState = {
  data?: AffectedAreaAccordionResponse;
  error?: boolean;
  loading?: boolean;
};

type AccordionLoadOptions = {
  force?: boolean;
};

type MahSelectionState = "checked" | "partial" | "unchecked";

interface ValidateAffectedAreasForSubmitParams {
  affectedAreaTags?: AffectedAreaTagSummaryResponse[] | null;
  isDraft: boolean;
  selectedSelections?: AffectedAreaSelectionFormValue[] | null;
}

export interface AffectedAreasProps {
  affectedAreaTags?: AffectedAreaTagSummaryResponse[] | null;
  form?: UseFormReturn<CreateStep2MdbFormValues>;
  loadAffectedAreaAccordion?: (
    tagCode: string,
    mahCode: string
  ) => Promise<AffectedAreaAccordionResponse>;
  readOnly?: boolean;
}

const getAccordionKey = (tagCode: string, mahCode: string) => `${tagCode}::${mahCode}`;

export const getAffectedAreaSelectionKey = (selection: AffectedAreaSelectionFormValue) =>
  [selection.tag_code, selection.mah_code, selection.barrier_code, selection.origin_code].join(
    "::"
  );

const mapRowToSelection = (row: AffectedAreaRowResponse): AffectedAreaSelectionFormValue => ({
  tag_code: row.tag_code,
  mah_code: row.mah_code,
  barrier_code: row.barrier_code,
  origin_code: row.origin_code,
});

export const getMahSelectionState = (
  rowSelectionKeys: string[],
  selectedSelectionKeys: string[]
): MahSelectionState => {
  if (!rowSelectionKeys.length) return "unchecked";

  const selectedCount = rowSelectionKeys.filter((rowId) =>
    selectedSelectionKeys.includes(rowId)
  ).length;

  if (selectedCount === rowSelectionKeys.length) return "checked";
  if (selectedCount > 0) return "partial";
  return "unchecked";
};

export const validateAffectedAreasForSubmit = ({
  affectedAreaTags,
  isDraft,
  selectedSelections,
}: ValidateAffectedAreasForSubmitParams) => {
  if (isDraft) return true;

  const requiredTagCodes =
    affectedAreaTags
      ?.filter((tag) => tag.has_available_options && tag.mahs.length)
      .map((tag) => tag.tag_code) ?? [];

  if (!requiredTagCodes.length) return true;

  const selectedTagCodes = new Set(
    (selectedSelections ?? []).map((selection) => selection.tag_code)
  );

  return requiredTagCodes.every((tagCode) => selectedTagCodes.has(tagCode));
};

interface MahCheckboxProps {
  checked: boolean;
  disabled: boolean;
  indeterminate: boolean;
  label: string;
  onChange: () => void;
}

const MahCheckbox: FC<MahCheckboxProps> = ({
  checked,
  disabled,
  indeterminate,
  label,
  onChange,
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      aria-label={label}
      checked={checked}
      className={styles.mahCheckbox}
      disabled={disabled}
      onChange={onChange}
      onClick={(event) => event.stopPropagation()}
      ref={checkboxRef}
      type="checkbox"
    />
  );
};

const AffectedAreasErrorMessage: FC<{
  form: UseFormReturn<CreateStep2MdbFormValues>;
}> = ({ form }) => {
  const { errors } = useFormState({ control: form.control, name: "affected_areas" });
  const errorMessage = errors.affected_areas?.message;

  if (!errorMessage) return null;

  return (
    <div className={styles.errorScrollTarget}>
      <input
        aria-hidden="true"
        className={styles.errorFocusInput}
        readOnly
        tabIndex={-1}
        type="text"
      />
      <p className={styles.errorMessage} role="alert">
        {errorMessage}
      </p>
    </div>
  );
};

export const AffectedAreas: FC<AffectedAreasProps> = ({
  affectedAreaTags,
  form,
  loadAffectedAreaAccordion,
  readOnly = false,
}) => {
  const containerClassName = readOnly ? `${styles.container} ${styles.readOnly}` : styles.container;
  const [accordionState, setAccordionState] = useState<Record<string, AccordionState>>({});
  const accordionStateRef = useRef<Record<string, AccordionState>>({});
  const [selectedSelections, setSelectedSelections] = useState<AffectedAreaSelectionFormValue[]>(
    () => form?.getValues("affected_areas") ?? []
  );

  useEffect(() => {
    if (!form || readOnly) return;

    form.register("affected_areas", {
      validate: (value) =>
        validateAffectedAreasForSubmit({
          affectedAreaTags,
          isDraft: form.getValues("draft") === true,
          selectedSelections: value,
        }) || translate("ctoStep2.affectedAreas.required"),
    });
  }, [affectedAreaTags, form, readOnly]);

  const updateAccordionState = useCallback(
    (
      getNextState: (currentState: Record<string, AccordionState>) => Record<string, AccordionState>
    ) => {
      setAccordionState((currentState) => {
        const nextState = getNextState(currentState);
        accordionStateRef.current = nextState;
        return nextState;
      });
    },
    []
  );

  const loadAccordion = useCallback(
    async (tagCode: string, mah: AffectedAreaMahResponse, options: AccordionLoadOptions = {}) => {
      const key = getAccordionKey(tagCode, mah.mah_code);
      const currentState = accordionStateRef.current[key];

      if (currentState?.data || currentState?.loading || (currentState?.error && !options.force)) {
        return;
      }

      updateAccordionState((current) => ({
        ...current,
        [key]: { loading: true },
      }));

      try {
        const data = loadAffectedAreaAccordion
          ? await loadAffectedAreaAccordion(tagCode, mah.mah_code)
          : { tag_code: tagCode, mah, rows: [] };

        updateAccordionState((current) => ({
          ...current,
          [key]: { data },
        }));
      } catch {
        updateAccordionState((current) => ({
          ...current,
          [key]: { error: true },
        }));
      }
    },
    [loadAffectedAreaAccordion, updateAccordionState]
  );

  useEffect(() => {
    if (!readOnly || !affectedAreaTags?.length) return;

    affectedAreaTags.forEach((tag) => {
      if (!tag.has_available_options) return;

      tag.mahs.forEach((mah) => {
        void loadAccordion(tag.tag_code, mah);
      });
    });
  }, [affectedAreaTags, loadAccordion, readOnly]);

  const updateSelectedSelections = (
    getNextSelections: (
      currentSelections: AffectedAreaSelectionFormValue[]
    ) => AffectedAreaSelectionFormValue[]
  ) => {
    setSelectedSelections((currentSelections) => {
      const nextSelections = getNextSelections(currentSelections);

      form?.setValue("affected_areas", nextSelections, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (
        form &&
        validateAffectedAreasForSubmit({
          affectedAreaTags,
          isDraft: form.getValues("draft") === true,
          selectedSelections: nextSelections,
        })
      ) {
        form.clearErrors("affected_areas");
      }

      return nextSelections;
    });
  };

  const toggleRowSelection = (row: AffectedAreaRowResponse) => {
    const rowSelection = mapRowToSelection(row);
    const rowSelectionKey = getAffectedAreaSelectionKey(rowSelection);

    updateSelectedSelections((currentSelections) =>
      currentSelections.some(
        (selection) => getAffectedAreaSelectionKey(selection) === rowSelectionKey
      )
        ? currentSelections.filter(
            (selection) => getAffectedAreaSelectionKey(selection) !== rowSelectionKey
          )
        : [...currentSelections, rowSelection]
    );
  };

  const toggleMahSelection = (
    rows: AffectedAreaRowResponse[],
    selectionState: MahSelectionState
  ) => {
    updateSelectedSelections((currentSelections) => {
      const rowSelectionKeys = new Set(
        rows.map((row) => getAffectedAreaSelectionKey(mapRowToSelection(row)))
      );
      const withoutMahRows = currentSelections.filter(
        (selection) => !rowSelectionKeys.has(getAffectedAreaSelectionKey(selection))
      );

      if (selectionState === "checked") return withoutMahRows;

      return [...withoutMahRows, ...rows.map(mapRowToSelection)];
    });
  };

  if (!affectedAreaTags?.length) return null;

  return (
    <section className={containerClassName} aria-label={translate("ctoStep2.affectedAreas.title")}>
      <SectionTitle>{translate("ctoStep2.affectedAreas.title")}</SectionTitle>

      {form && <AffectedAreasErrorMessage form={form} />}

      {affectedAreaTags.map((tag) => (
        <div className={styles.tagGroup} key={tag.tag_code}>
          <div className={styles.instruction}>
            <InfoOutlined className={styles.instructionIcon} />
            {readOnly ? (
              <span>
                {translate("ctoStep2.affectedAreas.readOnlyInstruction", {
                  tagCode: tag.tag_code,
                })}
              </span>
            ) : (
              <>
                <span>{translate("ctoStep2.affectedAreas.instructionPrefix")}</span>
                <span className={styles.instructionTag}>TAG: {tag.tag_code}</span>
              </>
            )}
          </div>

          {!tag.has_available_options || !tag.mahs.length ? (
            <p className={styles.emptyState}>{translate("ctoStep2.affectedAreas.noOptions")}</p>
          ) : (
            <div className={styles.accordionList}>
              {tag.mahs.map((mah) => {
                const key = getAccordionKey(tag.tag_code, mah.mah_code);
                const state = accordionState[key];
                const rows = state?.data?.rows ?? [];
                const rowSelectionKeys = rows.map((row) =>
                  getAffectedAreaSelectionKey(mapRowToSelection(row))
                );
                const selectedSelectionKeys = selectedSelections.map(getAffectedAreaSelectionKey);
                const selectionState = getMahSelectionState(
                  rowSelectionKeys,
                  selectedSelectionKeys
                );

                return (
                  <Accordion
                    className={styles.accordion}
                    disableGutters
                    key={key}
                    onChange={(_, expanded) => {
                      if (expanded) void loadAccordion(tag.tag_code, mah);
                    }}
                  >
                    <AccordionSummary
                      className={styles.summary}
                      expandIcon={<span className={styles.expandIcon} />}
                    >
                      <MahCheckbox
                        checked={selectionState === "checked"}
                        disabled={readOnly || !rows.length}
                        indeterminate={selectionState === "partial"}
                        label={`${mah.mah_code} ${translate("ctoStep2.affectedAreas.title")}`}
                        onChange={() => toggleMahSelection(rows, selectionState)}
                      />
                      <span className={styles.mahCode}>{mah.mah_code}</span>
                      <span className={styles.mahName}>{mah.mah_name}</span>
                    </AccordionSummary>

                    <AccordionDetails className={styles.details}>
                      {state?.loading && (
                        <p className={styles.stateMessage}>
                          {translate("ctoStep2.affectedAreas.loading")}
                        </p>
                      )}

                      {state?.error && (
                        <div className={styles.stateMessage}>
                          <p>{translate("ctoStep2.affectedAreas.loadError")}</p>
                          <button
                            className={styles.retryButton}
                            onClick={() => void loadAccordion(tag.tag_code, mah, { force: true })}
                            type="button"
                          >
                            {translate("ctoStep2.affectedAreas.retry")}
                          </button>
                        </div>
                      )}

                      {state?.data && !state.data.rows.length && (
                        <p className={styles.stateMessage}>
                          {translate("ctoStep2.affectedAreas.noOptions")}
                        </p>
                      )}

                      {!!state?.data?.rows.length && (
                        <div className={styles.rows}>
                          {state.data.rows.map((row) => (
                            <div className={styles.row} key={row.row_id}>
                              <span className={styles.rowMarker} />
                              <input
                                aria-label={row.barrier_name}
                                checked={selectedSelectionKeys.includes(
                                  getAffectedAreaSelectionKey(mapRowToSelection(row))
                                )}
                                className={styles.rowCheckbox}
                                disabled={readOnly}
                                onChange={() => toggleRowSelection(row)}
                                type="checkbox"
                              />
                              <span className={styles.barrier}>{row.barrier_name}</span>
                              <ArrowForward className={styles.arrow} />
                              <span className={styles.consequence}>{row.origin_name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </section>
  );
};
